import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { GrooveListLinkDirective } from '@groove/angular';
import { DATA_CONFIG, SOCIAL_KEYS } from '../../injection-tokens';
import { NAV_GROUPS, type NavGroup } from '../nav-data';

// Disclosure semantics, not a menu — mirrors the desktop top-nav. Each group
// header is a hand-rolled disclosure button (aria-expanded/aria-controls)
// revealing a region of Tab-reachable <a>s; no roving arrow-key focus. This
// keeps the Blog link (a plain <a> peer) in the natural Tab order and lets
// Escape collapse an open section before dismissing the drawer.
@Component({
  selector: 'app-mobile-drawer',
  imports: [RouterLink, RouterLinkActive, GrooveListLinkDirective],
  templateUrl: './mobile-drawer.component.html',
  styleUrl: './mobile-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileDrawerComponent {
  /** Shared nav model — single source of truth for the drawer disclosures. */
  protected readonly navGroups: readonly NavGroup[] = NAV_GROUPS;

  private readonly dataConfig = inject(DATA_CONFIG);

  // Social links live in the drawer (and the footer) on mobile — they were
  // pulled out of the chrome shelf below the nav breakpoint to keep it short.
  // Mirrors the top-nav SOCIAL_KEYS pattern; icons applied as a CSS mask-image.
  protected readonly socialLinks = SOCIAL_KEYS.map((key) => ({
    key,
    label: this.dataConfig[key].displayText,
    href: this.dataConfig[key].link,
    iconUrl: `url('/social-icons/${key}.svg')`,
  }));

  // Contact — single source of truth from DATA_CONFIG; mirrors the derivation
  // in footer.component.ts so phone/email stay in sync across the entire site.
  protected readonly phoneDisplay = this.dataConfig.phone;
  protected readonly phoneHref = this.dataConfig.phoneHref;
  protected readonly emailDisplay = this.dataConfig.email;
  protected readonly emailHref = this.dataConfig.emailHref;

  /** Label of the currently open section, or null when all are collapsed. */
  protected readonly openSection = signal<string | null>(null);

  /**
   * Fires when the underlying `<dialog>` emits its native `close` event
   * (Escape, close button click, or `close()` API call). Lets the parent
   * sync its own open-state signal back to false.
   */
  readonly drawerClosed = output();

  private readonly dialog =
    viewChild.required<ElementRef<HTMLDialogElement>>('drawerDialog');

  open(): void {
    const el = this.dialog().nativeElement;
    if (!el.open) {
      el.showModal();
    }
  }

  close(): void {
    const el = this.dialog().nativeElement;
    if (el.open) {
      el.close();
    }
  }

  /** Single-open disclosure: clicking an open section closes it. */
  protected toggleSection(label: string): void {
    this.openSection.update((cur) => (cur === label ? null : label));
  }

  protected sectionPanelId(label: string): string {
    return `drawer-panel-${label.toLowerCase().replace(/\s+/g, '-')}`;
  }

  /**
   * Escape is collapse-then-close: when a section is open, the first Escape
   * collapses it and stays in the drawer (preventDefault stops the native
   * `<dialog>` cancel from also closing the drawer). With nothing open, Escape
   * dismisses the drawer.
   */
  protected onEscape(event: Event): void {
    if (this.openSection() !== null) {
      event.preventDefault();
      this.openSection.set(null);
      return;
    }
    this.close();
  }

  /**
   * Belt-and-suspenders for the native `<dialog>` cancel event: some browsers
   * fire `cancel` (which closes the dialog) on Escape independently of the
   * keydown handler. When a section is open we cancel that too, so the first
   * Escape only collapses the section.
   */
  protected onCancel(event: Event): void {
    if (this.openSection() !== null) {
      event.preventDefault();
      this.openSection.set(null);
    }
  }

  /**
   * Closes the drawer on two implicit dismissals:
   * - Backdrop click — native `<dialog>` doesn't auto-close on backdrop
   *   click. When the click target IS the dialog element itself, the
   *   click happened on the backdrop (clicks inside the dialog's content
   *   bubble through specific children, not the dialog itself).
   * - Anchor click — when the user clicks a nav link inside the drawer,
   *   close so the navigated-to page is visible. The disclosure triggers
   *   are `<button>` elements (not anchors), so they keep the drawer open.
   */
  protected handleDialogClick(event: MouseEvent): void {
    const dialogEl = this.dialog().nativeElement;
    const target = event.target as HTMLElement;
    if (target === dialogEl) {
      this.close();
      return;
    }
    if (target.closest('a')) {
      this.close();
    }
  }
}
